import type { ShopifyAdminAppRouter } from "@loot-os/functions/src/dashboard-api/trpc/shopifyAdmin/router.js";
import type { inferRouterOutputs } from "@trpc/server";

type Outputs = inferRouterOutputs<ShopifyAdminAppRouter>;
type BoxStatus = Outputs["boxes"]["list"][number]["status"];
type PurchaseStatus = Outputs["purchases"]["list"][number]["status"];
type TransferStatus = Outputs["purchases"]["transfers"][number]["status"];
type TransferReason = Outputs["purchases"]["transfers"][number]["reason"];
type DispatchStatus = Outputs["shipOrders"]["list"][number]["dispatchStatus"];
type OrderStatus = Outputs["shipOrders"]["list"][number]["orderStatus"];

/** A badge's words and Polaris tone. */
export interface StatusLabel {
  label: string;
  tone: "neutral" | "info" | "success" | "warning" | "critical";
}

/** A box's lifecycle, in the merchant's words. */
export const BOX_STATUS: Record<BoxStatus, StatusLabel> = {
  draft: { label: "Not ready yet", tone: "warning" },
  pre_release: { label: "Pre-release", tone: "info" },
  active: { label: "Ready", tone: "success" },
  archived: { label: "Archived", tone: "neutral" },
};

/** Where one box bought at checkout is now. */
export const PURCHASE_STATUS: Record<PurchaseStatus, StatusLabel> = {
  processing: { label: "Processing", tone: "info" },
  unrevealed: { label: "Not opened yet", tone: "info" },
  converted: { label: "Turned into store credit", tone: "neutral" },
  revealed: { label: "Opened, not claimed", tone: "warning" },
  claimed: { label: "Item claimed", tone: "success" },
  sold: { label: "Sold back for store credit", tone: "neutral" },
  voided: { label: "Refunded", tone: "neutral" },
};

/** A store credit transfer that has not landed. */
export const TRANSFER_STATUS: Record<TransferStatus, StatusLabel> = {
  pending: { label: "Waiting", tone: "info" },
  applied: { label: "Applied", tone: "success" },
  failed: { label: "Failed", tone: "critical" },
};

/** Why store credit is moving: given, clawed back, or paid for a claim. */
export const TRANSFER_REASON: Record<TransferReason, string> = {
  sell_back: "Item sold back",
  conversion: "Box turned into credit",
  sweep: "Leftover balance",
  refund: "Refund",
  out_of_stock: "Revealed item was sold out",
  reveal: "Box revealed (price held as credit)",
  claim_payment: "Revealed item paid for",
  top_up: "Store credit bought",
};

/** Whether Loot OS managed to create the $0 order in the store. */
export const DISPATCH_STATUS: Record<DispatchStatus, StatusLabel> = {
  pending: { label: "Being created", tone: "info" },
  created: { label: "In your orders", tone: "success" },
  failed: { label: "Couldn't create", tone: "critical" },
  out_of_stock: { label: "Sold out, paid as store credit", tone: "warning" },
};

/** A purchase whose revealed item was sold out at ship time, in place of its `sold` label. */
export const SOLD_OUT_PURCHASE: StatusLabel = {
  label: "Item sold out, paid as store credit",
  tone: "warning",
};

/**
 * Whether "Try again" does anything: a dispatch not yet created, or an out-of-stock substitution
 * whose note on the order in Shopify is still owed.
 */
export function canRetryShipOrder(o: {
  dispatchStatus: DispatchStatus;
  shopifyAnnotatedAt: string | Date | null;
}): boolean {
  if (o.dispatchStatus === "out_of_stock") return o.shopifyAnnotatedAt === null;
  return o.dispatchStatus !== "created";
}

/** The fulfillment order's own progress. */
export const ORDER_STATUS: Record<OrderStatus, StatusLabel> = {
  PENDING: { label: "To ship", tone: "info" },
  PROCESSING: { label: "Preparing", tone: "info" },
  PRESHIPPED: { label: "Ready to ship", tone: "info" },
  SHIPPED: { label: "Shipped", tone: "success" },
  SUCCESS: { label: "Complete", tone: "success" },
  CANCELLED: { label: "Cancelled", tone: "neutral" },
  UNAPPROVED: { label: "Needs approval", tone: "warning" },
  APPROVED: { label: "Approved", tone: "info" },
  HOLD: { label: "On hold", tone: "warning" },
  FAIL: { label: "Failed", tone: "critical" },
};
